import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';

import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';

import { get } from 'lodash-es';
import { Observable, of, throwError } from 'rxjs';
import { switchMap, take } from 'rxjs/operators';

import { KeyRefreshEnum } from '../../enums/key-refresh';
import * as fromPropertyProfileActions from '../../store/actions/property-profile.actions';
import * as fromWalkingFarmActions from '../../store/actions/walking-farm.actions';
import { ShouldRefreshKey } from '../../typings/property-force-api';
import { MiddlewareMessageService } from '../services/middleware-message.service';

@Injectable({
  providedIn: 'root'
})
export class KeyRefreshInterceptor implements HttpInterceptor {
  private actions = inject(Actions);
  private middlewareMessageService = inject(MiddlewareMessageService);
  private store = inject(Store);

  intercept(request: HttpRequest<unknown>, next: HttpHandler) {
    let handledRequest: Observable<HttpEvent<unknown>>;

    if ([KeyRefreshEnum.PropertyProfile, KeyRefreshEnum.Farm].includes(request.context.get(ShouldRefreshKey))) {
      handledRequest = next.handle(request).pipe(
        switchMap((event) => {
          let handledEvent = of(event);
          if (event instanceof HttpResponse) {
            const { isInvalidKey } = this.middlewareMessageService.processStatus(
              event.body?.d?.StatusCode,
              event.body?.d?.Status
            );
            if (isInvalidKey) {
              this.refreshTheKey(request.context.get(ShouldRefreshKey));
              handledEvent = this.remakeRequestWithNewKey(request, event, next);
            }
          }
          return handledEvent;
        })
      );
    } else {
      handledRequest = next.handle(request);
    }
    return handledRequest;
  }

  refreshTheKey(header: KeyRefreshEnum) {
    switch (header) {
      case KeyRefreshEnum.PropertyProfile:
        this.store.dispatch(fromPropertyProfileActions.refreshKey());
        break;
      case KeyRefreshEnum.Farm:
        this.store.dispatch(fromWalkingFarmActions.refreshKey());
        break;
    }
  }

  remakeRequestWithNewKey(request: HttpRequest<unknown>, event: HttpResponse<unknown>, next: HttpHandler) {
    return this.actions
      .pipe(ofType(fromPropertyProfileActions.refreshKeySuccess, fromWalkingFarmActions.refreshKeySuccess))
      .pipe(
        take(1),
        switchMap(({ payload: { key } }) => {
          let handledEvent: Observable<HttpResponse<unknown>> | Observable<HttpEvent<unknown>> = of(event);
          if (key) {
            handledEvent = next
              .handle(
                request.clone({
                  params: request.params.set('key', key)
                })
              )
              .pipe(
                switchMap((event) => {
                  const handledEvent = of(event);
                  if (event instanceof HttpResponse) {
                    const { error, hasError } = this.middlewareMessageService.processStatus(
                      event.body?.d?.StatusCode,
                      event.body?.d?.Status
                    );
                    if (hasError) {
                      return throwError(() => error);
                    }
                  }
                  return handledEvent;
                })
              );
          } else {
            const { error } = this.middlewareMessageService.processStatus(
              get<HttpResponse<unknown>, string>(event, 'body.d.StatusCode'),
              get<HttpResponse<unknown>, string>(event, 'body.d.Status')
            );
            return throwError(() => error);
          }
          return handledEvent;
        })
      );
  }
}
